import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MapPin, Mail } from "lucide-react";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import { Button } from "../components/ui/button";

const LEADERSHIP = [
  { name: "M. Gilani", role: "Founder & Chief Executive", bio: "Twenty years building security software for teams that actually ship." },
  { name: "S. Ortiz", role: "Chief Security Officer", bio: "Former pentester, current product person. Author of the ArgusSecure DAST safety policy." },
  { name: "J. Patel", role: "Head of Engineering", bio: "Built distributed scanning at scale. Argues with everyone about types." },
  { name: "R. Ahmed", role: "Head of Research", bio: "Tracks the CVE feed. Wrote half the SCA matching rules in this product." },
];

const VALUES = [
  { h: "Evidence over claims.", p: "If we say it's exploitable, we show the request and response. If we can't show it, we don't say it." },
  { h: "Ship the fix, not the framework.", p: "Every finding has a remediation. Pointing at a problem isn't a product — solving it is." },
  { h: "Hard controls beat policy text.", p: "DAST is gated on domain verification at the scanner layer. Not in the UI. Not in the AUP. In the code." },
  { h: "Price like a teammate.", p: "Free for one project. $49/mo for unlimited. Enterprise is for people who genuinely need on-prem and SSO." },
];

export default function About() {
  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col">
      <MarketingHeader />
      <section className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <p className="text-xs uppercase tracking-wider text-gold mb-3 font-semibold">Company</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight max-w-3xl" data-testid="about-title">
          The people protecting you.
        </h1>
        <p className="mt-6 text-lg text-ink/70 max-w-2xl">
          ArgusSecure is built by <strong className="text-ink">GSGD FIELD SOLUTIONS ENGINEERING</strong>. The ArgusSecure team has spent careers watching AppSec tools
          drift away from the people who actually use them. ArgusSecure is the rebuke.
        </p>
      </section>

      <section className="border-y border-line bg-panel">
        <div className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-4xl mb-2">What we believe.</h2>
          <p className="text-ink/60 mb-8">Four principles that drive every decision in the product.</p>
          <div className="grid md:grid-cols-2 gap-6" data-testid="company-values">
            {VALUES.map((v) => (
              <div key={v.h} className="rounded-xl border border-line bg-paper p-6">
                <h3 className="font-display text-2xl mb-2">{v.h}</h3>
                <p className="text-ink/70">{v.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-4xl mb-2">Leadership.</h2>
        <p className="text-ink/60 mb-8">Four humans you can email.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="leadership-grid">
          {LEADERSHIP.map((p) => (
            <div key={p.name} className="rounded-xl border border-line bg-panel p-5" data-testid={`leader-${p.name.replace(/[^a-z]/gi, "").toLowerCase()}`}>
              <div className="w-16 h-16 rounded-full bg-raised mb-4 flex items-center justify-center font-display text-2xl text-gold">
                {p.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}
              </div>
              <div className="font-display text-xl">{p.name}</div>
              <div className="text-xs uppercase tracking-wider text-ink/40 mb-3">{p.role}</div>
              <p className="text-sm text-ink/70">{p.bio}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-panel border-t border-line">
        <div className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-3 gap-8" data-testid="company-contact">
          <div>
            <h3 className="font-display text-2xl mb-3">Talk to security.</h3>
            <p className="text-ink/60 text-sm mb-4">Compliance question? On-prem ask? Vendor security review? Real humans on this thread.</p>
            <Link to="/abuse" className="text-gold inline-flex items-center gap-1 text-sm hover:text-gold2" data-testid="company-contact-cta">Open a thread →</Link>
          </div>
          <div>
            <h3 className="font-display text-2xl mb-3 flex items-center gap-2"><Mail size={18} className="text-gold" /> Reach us.</h3>
            <ul className="text-sm text-ink/70 space-y-1">
              <li>hello@argus.security</li>
              <li>security@argus.security (vuln reports)</li>
              <li>privacy@argus.security</li>
              <li>legal@argus.security</li>
            </ul>
          </div>
          <div>
            <h3 className="font-display text-2xl mb-3 flex items-center gap-2"><MapPin size={18} className="text-gold" /> Where we are.</h3>
            <p className="text-sm text-ink/70">ArgusSecure, c/o GSGD FIELD SOLUTIONS ENGINEERING.<br />Distributed across three continents.<br />Headquartered nowhere in particular.</p>
          </div>
        </div>
        <div className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 pb-16 text-center">
          <Link to="/signup">
            <Button className="bg-gold text-paper hover:bg-gold2 h-12 px-6 font-semibold" data-testid="about-cta">
              Start My Free Scan <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
